// Config déclarative des colonnes pour la vue compacte
// Chaque colonne a un id unique, un label, et comment rendre la cellule / l'en-tête.

import React from "react";
import { FaCopy } from "react-icons/fa";

export const STORAGE_KEY = "monappli:listing_adherents:columns:v1";

const parseDateOnlyLocal = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return null;
  const s = String(yyyy_mm_dd).slice(0, 10); // sécurise si "YYYY-MM-DDTHH..."
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d); // ✅ LOCAL, pas UTC
};

// Colonnes proposées (hors "identity" & "actions" qui restent fixes)
export const ALL_COLUMNS = [
  {
    id: "status",
    label: "Statut",
    minWidth: 120,
    maxWidth: 180,
    sortable: false,
    renderHeader: () => "Statut",
    renderCell: (a, ctx) => {
      const {
        editingStatusId,
        setEditingStatusId,
        handleStatusChange,
        getStatusColor,
      } = ctx;
      return (
        <div className="px-3 py-2">
          {editingStatusId === a.id ? (
            <select
              value={a.statut_inscription}
              onChange={(e) => handleStatusChange(a.id, e.target.value)}
              onBlur={() => setEditingStatusId(null)}
              className="w-full rounded-sm border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-xs"
              autoFocus
            >
              <option value="Licencié">Licencié</option>
              <option value="Inscrit">Inscrit</option>
              <option value="Cours d'essai">Cours d'essai</option>
              <option value="Licencié - Arrêt">Licencié - Arrêt</option>
              <option value="Non Licencié - Arrêt">Non Licencié - Arrêt</option>
              <option value="Licencié Hors Club">Licencié Hors Club</option>
              <option value="Ancien Licencié">Ancien Licencié</option>
              <option value="Ancien Licencié et Arrêt">
                Ancien Licencié et Arrêt
              </option>
            </select>
          ) : (
            <span
              className={`inline-block px-2 py-0.5 whitespace-nowrap text-[11px] font-semibold rounded-sm cursor-pointer ${getStatusColor(
                a.statut_inscription,
              )}`}
              onClick={() => setEditingStatusId(a.id)}
              title="Cliquer pour modifier"
            >
              {a.statut_inscription}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: "groups",
    label: "Groupes",
    minWidth: 260,
    maxWidth: 360,
    sortable: false,
    renderHeader: () => "Groupes",
    renderCell: (a, ctx) => {
      const { getGroupPillClasses, openChangeGroupModal } = ctx;
      return (
        <div className="px-3 py-2">
          <div
            className="flex flex-wrap gap-1 cursor-pointer"
            title="Gérer les groupes"
            onClick={() => openChangeGroupModal(a.id)}
          >
            {(a.Groups || []).length > 0 ? (
              a.Groups.map((g) => {
                const { bg, text, ring } = getGroupPillClasses(g);
                return (
                  <span
                    key={g.id || g.nom}
                    className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded ${bg} ${text} ${ring} hover:opacity-90`}
                  >
                    {g.nom}
                  </span>
                );
              })
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-rose-600 text-white hover:opacity-90">
                Sans groupe (clic)
              </span>
            )}
          </div>
        </div>
      );
    },
  },
  {
    id: "email",
    label: "Email",
    minWidth: 300,
    maxWidth: 560,
    sortable: false,
    renderHeader: (_unused, ctx) => (
      <div className="px-3 py-2 flex items-center gap-2">
        <span>Contact</span>
        <button
          onClick={ctx.copyFilteredEmails}
          className="text-[11px] p-1 rounded hover:bg-white/40 focus:outline-none focus:ring-2 focus:ring-violet-400"
          title="Copier les emails filtrés"
        >
          <FaCopy />
        </button>
      </div>
    ),
    renderCell: (a) => (
      <div className="px-3 py-2">
        <div className="truncate">{a.email || "—"}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {(a.Users || []).map((u) => `${u.nom} ${u.prenom}`).join(", ") || "—"}
          {" • "}
          {a.createdAt
            ? new Date(a.createdAt).toLocaleDateString("fr-FR")
            : "—"}
        </div>
      </div>
    ),
  },
  {
    id: "birthdate",
    label: "Naissance",
    minWidth: 140,
    maxWidth: 200,
    sortable: false,
    renderHeader: () => "Naissance",
    renderCell: (a, ctx) => {
      const { isOutOfAllGroups, activeSeason } = ctx;
      const bad = a.date_naissance
        ? isOutOfAllGroups(a.date_naissance, a.Groups, activeSeason)
        : false;
      const dateStr = a.date_naissance
        ? (parseDateOnlyLocal(a.date_naissance)?.toLocaleDateString("fr-FR") ??
          "—")
        : "—";
      return (
        <div className="px-3 py-2">
          <span
            className={bad ? "bg-red-600 text-white px-1 rounded" : ""}
            title={
              bad ? "Âge incompatible avec le(s) groupe(s) assigné(s)" : ""
            }
          >
            {dateStr}
          </span>
        </div>
      );
    },
  },
  {
    id: "age",
    label: "Âge",
    minWidth: 80,
    maxWidth: 120,
    sortable: false,
    renderHeader: () => "Âge",
    renderCell: (a, ctx) => {
      const { ageOn, getSeasonRefDate, activeSeason } = ctx;
      if (!a.date_naissance) return <div className="px-3 py-2">—</div>;
      const ref = getSeasonRefDate(activeSeason);
      const age = ageOn(a.date_naissance, ref);
      return <div className="px-3 py-2">{age ?? "—"}</div>;
    },
  },
  {
    id: "phone",
    label: "Téléphone",
    minWidth: 150,
    maxWidth: 220,
    sortable: false,
    renderHeader: () => "Téléphone",
    renderCell: (a) => (
      <div className="px-3 py-2 truncate">{a.tel || a.phone || "—"}</div>
    ),
  },
  {
    id: "commune",
    label: "Commune",
    minWidth: 160,
    maxWidth: 220,
    sortable: false,
    renderHeader: () => "Commune",
    renderCell: (a) => (
      <div className="px-3 py-2 truncate">{a.commune || "—"}</div>
    ),
  },
  {
    id: "licence",
    label: "Licence",
    minWidth: 160,
    maxWidth: 220,
    sortable: false,
    renderHeader: () => "Licence",
    renderCell: (a) => (
      <div className="px-3 py-2 truncate">{a.licence_number || "—"}</div>
    ),
  },
  {
    id: "createdAt",
    label: "Créé le",
    minWidth: 140,
    maxWidth: 200,
    sortable: false,
    renderHeader: () => "Créé le",
    renderCell: (a) => (
      <div className="px-3 py-2">
        {a.createdAt ? new Date(a.createdAt).toLocaleDateString("fr-FR") : "—"}
      </div>
    ),
  },
  {
    id: "referents",
    label: "Référents",
    minWidth: 220,
    maxWidth: 360,
    sortable: false,
    renderHeader: () => "Référents",
    renderCell: (a) => (
      <div className="px-3 py-2 truncate">
        {(a.Users || []).map((u) => `${u.nom} ${u.prenom}`).join(", ") || "—"}
      </div>
    ),
  },
];

// Colonnes visibles par défaut
export const DEFAULT_VISIBLE_COLUMN_IDS = [
  "status",
  "groups",
  "email",
  "birthdate",
];
